import type { TenantSummary } from "@storytell/types";
import {
  StDropdownItem,
  StDropdownPortal,
  StDropdownSub,
  StDropdownSubContent,
  StDropdownSubTrigger,
} from "@storytell/ui/dropdown";
import { TbCheck, TbChevronRight } from "solid-icons/tb";
import { type Component, For, Show } from "solid-js";
import { Avatar } from "~/components/avatar/Avatar";
import { NavRowLabel } from "~/components/navigation/NavRowLabel";
import { useOrgScope } from "~/hooks/useOrgScope";
import { useI18n } from "~/i18n";
import { useWire } from "~/wire";

// Past this many rows the flyout scrolls, and the modal becomes the better way
// to find one.
const OVERFLOW_THRESHOLD = 8;

const FLYOUT_ROW =
  "cursor-pointer flex items-center gap-2.5 rounded-md px-2 py-2 text-sm text-primary-text transition-colors " +
  "outline-none hover:bg-canvas-hover data-highlighted:bg-canvas-hover";

/**
 * The user menu's header for someone who belongs to more than one organization:
 * it names the current one and opens the switcher as a side flyout.
 */
export const UserAccountOrgSwitcher: Component<{
  current: TenantSummary;
  onOpenOrganizations: () => void;
}> = (props) => {
  const wire = useWire();
  const { t } = useI18n();
  const scope = useOrgScope();

  const isCurrent = (tenant: TenantSummary) => tenant.organizationId === props.current.organizationId;

  return (
    <StDropdownSub gutter={4}>
      <StDropdownSubTrigger class="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors outline-none hover:bg-canvas-hover data-highlighted:bg-canvas-hover">
        <Avatar src={wire.auth?.user()?.avatarUrl} name={wire.auth?.user()?.displayName} size="sm" />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium text-primary-text">
            {wire.auth?.user()?.displayName || t("common.you")}
          </span>
          <span class="block truncate text-xs text-muted-text">{scope.organizationName(props.current)}</span>
        </span>
        <TbChevronRight class="size-4 shrink-0 text-muted-text" aria-hidden />
      </StDropdownSubTrigger>

      {/* The parent menu is force-mounted for its exit animation, so the flyout
          renders while closed and has to hide itself. */}
      <StDropdownPortal>
        <StDropdownSubContent class="z-50 hidden max-h-75 w-64 overflow-y-auto rounded-lg border border-primary-border bg-tile px-2 py-1 text-sm text-primary-text shadow-xl outline-none data-expanded:block">
          <div class="truncate px-2 py-1.5 text-xs text-muted-text" title={wire.auth?.user()?.primaryEmail}>
            {wire.auth?.user()?.primaryEmail}
          </div>

          <div class="my-1 border-t border-primary-border" />

          <For each={scope.organizations()}>
            {(tenant) => (
              <StDropdownItem
                onClick={() => scope.switchToOrganization(tenant.organizationId)}
                aria-current={isCurrent(tenant) || undefined}
                class={FLYOUT_ROW}
              >
                <TbCheck class="size-4 shrink-0" classList={{ invisible: !isCurrent(tenant) }} />
                <NavRowLabel
                  name={scope.organizationName(tenant)}
                  detail={scope.organizationRowDetail(tenant)}
                  current={isCurrent(tenant)}
                />
              </StDropdownItem>
            )}
          </For>

          <Show when={scope.organizations().length > OVERFLOW_THRESHOLD}>
            <div class="my-1 border-t border-primary-border" />
            <StDropdownItem onClick={props.onOpenOrganizations} class={FLYOUT_ROW}>
              <span class="text-sm">{t("organization.switcher.see_all")}</span>
            </StDropdownItem>
          </Show>
        </StDropdownSubContent>
      </StDropdownPortal>
    </StDropdownSub>
  );
};
